import React from 'react';
import { useMidway } from './useMidway';
import { useServiceSessions } from './useServiceSessions';

export interface ServiceSessionGateProps {
  /** Rendered while Midway and the service warm-up are in flight. */
  readonly loadingFallback: React.ReactNode;
  /** Rendered when the warm-up fails. Receives the underlying error. */
  readonly errorFallback: (error: Error) => React.ReactNode;
  readonly children: React.ReactNode;
}

/**
 * Blocking gate that renders NOTHING but `loadingFallback` until Midway has
 * resolved and every required service has established its session cookie.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 *
 * Each backing service issues its own SameSite session cookie the first time
 * it sees a Midway-authenticated request. Until that cookie exists, a
 * credentialed cross-origin call to that service is answered by the ALB with a
 * redirect to the IdP rather than by the service — and a cross-origin redirect
 * carries no CORS headers, so the caller sees an opaque CORS failure with no
 * status to inspect.
 *
 * React Query treats that as a normal failure and burns its retry budget. If
 * the retries are exhausted before the warm-up finishes, the query settles
 * into a permanent error state and never refetches, even though the cookie
 * has since arrived. The app then shows an auth error with a perfectly good
 * session in the jar.
 *
 * The only robust defence is structural: make it impossible for any service
 * call to be issued before the warm-up completes. A component that never
 * mounts cannot fire a query. That is precisely what the legacy desktop shell
 * does — `AuthHandler` renders only a loading screen until
 * `authStatus === 'ready'`, so `UserProvider` and every Orbit hook beneath it
 * are physically unable to run early.
 *
 * ── Why it must sit ABOVE MobileI18nProvider ──────────────────────────────
 *
 * `MobileI18nProvider` resolves the user's preferred language, which means it
 * issues an Orbit `user-preferences` call of its own. Placed below the auth
 * gate that would be fine; placed above it, that call escapes the gate
 * entirely. This gate therefore has to be the outermost thing in the tree
 * that touches a service.
 *
 * The consequence is that this gate cannot use `useMessages` — it renders
 * above `MbmProvider`, so no translation bundle is available yet. Its copy is
 * supplied by the caller as plain strings. That matches the legacy shell,
 * whose boot screen is also untranslated, and it only affects the brief
 * pre-session spinner; everything after the gate is fully localized.
 *
 * ── Interleaving ──────────────────────────────────────────────────────────
 *
 * The point of the gate is that there is no interleaving to reason about: no
 * service call can be in flight during warm-up, because nothing that makes one
 * has mounted. The property worth asserting in tests is exactly that —
 * children are absent from the tree until `isReady`.
 *
 * `useServiceSessions` dedupes the warm-up in a module-level cache keyed by
 * alias, so the `useServiceSessions` calls that already exist further down the
 * tree resolve from that cache instead of starting a second warm-up.
 */
export function ServiceSessionGate({
  loadingFallback,
  errorFallback,
  children,
}: ServiceSessionGateProps): JSX.Element {
  const { userAlias: alias, isError: isMidwayError, error: midwayError } = useMidway();
  const {
    isReady,
    isError: isSessionError,
    error: sessionError,
  } = useServiceSessions(alias);

  if (isMidwayError && midwayError) return <>{errorFallback(midwayError)}</>;
  if (isSessionError && sessionError) return <>{errorFallback(sessionError)}</>;
  if (!isReady) return <>{loadingFallback}</>;

  return <>{children}</>;
}
